import {createSelector} from "reselect"
import * as UserSchemas from "schemas/user"
import {sortBy} from "lodash"
import * as WorkspaceSelectors from 'redux/workspace.selector'
import {RootState} from "redux/store"
import {ClientWorkspace} from "redux/models/appEnvironment"
import {StringIndex} from "redux/models/workspace"
import {ACESTargetType, Environment, UserDtoSummaryWithAces} from "components/admin/user/UserManager.types"
import {UACWithAces} from "../hooks/useUACWithAces";
import {DatamodelDto} from "@biron-data/bqconf";
import {getUserFullName} from "../commons/getUserFullName";

export const getAssocClients = createSelector(
  (state: RootState) => state.users.entities,
  (entities) => sortBy(
    UserSchemas.denormalizeAssocClientResumes(Object.keys(entities.userAssocClients), entities) as UACWithAces[],
    [({user}) => user.lastName.toLocaleLowerCase(), ({user}) => user.firstName.toLocaleLowerCase()],
  ),
)

export type ConsolidatedAces = (Omit<UserDtoSummaryWithAces, "aces"> & {
  fullName: string
  aces: (UserDtoSummaryWithAces["aces"][0] & {
    targetType: ACESTargetType
    targetId: string
    targetObject: {
      id: number
      name: string
    } | null
  })[]
})
export const getConsolidatedAces = (
  aces: UserDtoSummaryWithAces[],
  workspaces: ClientWorkspace[],
  environment: Environment,
  datamodels: DatamodelDto[]): ConsolidatedAces[] => {
  const workspacesById: StringIndex<ClientWorkspace> = workspaces
    .reduce((acc, w) => ({
      ...acc,
      [w.id]: w,
    }), {})
  const datamodelsByCode: StringIndex<ClientWorkspace> = datamodels.reduce((acc, d) => ({
    ...acc,
    [d.code]: d,
  }), {})

  return sortBy(
    aces,
    [({lastName}) => lastName.toLocaleLowerCase(), ({firstName}) => firstName.toLocaleLowerCase()],
  ).map((ace) => {
    return {
      ...ace,
      fullName: getUserFullName(ace.firstName, ace.lastName),
      aces: ace.aces.map((localAces => {
        const targetElements: string[] = localAces.target.split(':')
        if (targetElements[0] === "datamodel") {
          const [targetType, environmentId, datamodelCode] = targetElements
          return environment.id === Number(environmentId) ? ({
            ...localAces,
            targetType,
            targetId: datamodelCode,
            targetObject: datamodelsByCode[datamodelCode],
          }) : undefined
        }
        const [targetType, id] = targetElements
        if (targetType === 'environment') {
          return Number(id) === environment.id ? ({
            ...localAces,
            targetType,
            targetId: id,
            targetObject: environment.id && environment.name ? {
              id: environment.id,
              name: environment.name
            } : null,
          }) : undefined
        }

        if (targetType === 'workspace') {
          const matchingWorkspace = workspacesById[id]
          return matchingWorkspace ? ({
            ...localAces,
            targetType,
            targetId: id,
            targetObject: matchingWorkspace,
          }) : undefined
        }

        if (targetType === 'app') {
          return ({
            ...localAces,
            targetType,
            targetId: id,
            targetObject: null,
          })
        }

        return undefined
      })).filter(a => a) as ConsolidatedAces['aces']
    }
  })
}
export const getAcesListSelector = createSelector(
  (state: RootState) => state.users.userWithAces,
  (state: RootState) => WorkspaceSelectors.getAvailablesForAce(state as never),
  (state: RootState) => state.appEnvironment?.environment,
  (state: RootState) => state.environment?.datamodels,
  (aces, workspaces: ClientWorkspace[], environment: Environment, datamodels): ConsolidatedAces[] => {
    return getConsolidatedAces(aces, workspaces, environment, datamodels)
  },
)
